import apiV2 from '../api/v2';
import type { PracticeRiskStatus } from './practiceRisk';

// Accounts > Practice detail (Details tab). The GET/PATCH payload is
// block-structured (mirrors the v2 patient GET): a handful of base fields
// plus five named blocks. Grouping into UI cards and every human-readable
// label are owned entirely on this side, in
// components/accounts/practice/details/fieldDefinitions.ts.
//
// `name` is the only editable field that stays top-level - it was previously
// duplicated as a separate `practiceName` field; both mirrored the same
// column, so that field was removed and `name` became the single writable
// source. drFirstName/drLastName/name are NOT NULL columns (never come back
// null, and can't be cleared to null on PATCH - only replaced; see
// fieldDefinitions.ts's `required` flag). dmPracticeId/dmDoctorId are Monday
// "numbers" columns underneath but contractually numeric *strings* on the
// wire (external DM system IDs, rendered as opaque mono text like billId -
// never arithmetic). daysNet looks numeric but is a Monday status column
// with compound preset labels (e.g. "30,60,90"), validated the same way as
// the other enum fields - see fieldDefinitions.ts's hardcoded option lists.
// `email2` (Secondary email) is rendered as of the D2 redesign - see
// fieldDefinitions.ts's practice group; `network` remains wire-only.

export interface PracticeContactBlock {
    phone: string | null;
    email: string | null;
    email2: string | null;
}

export interface PracticeAddressBlock {
    address: string | null;
    addressDetails: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
}

export interface PracticeEquipmentBlock {
    scanner: string | null;
    hasItero: string | null;
}

export interface PracticeDoctorBlock {
    drFirstName: string;
    drLastName: string;
    drEmail: string | null;
    designPreferences: string | null;
}

export interface PracticeAccountBlock {
    dmPracticeId: string | null;
    dmDoctorId: string | null;
    billId: string | null;
    billingEmail: string | null;
    ach: string | null;
    sparkAcct: string | null;
    invisalignAcct: string | null;
    accountType: string | null;
    network: string | null;
    version: string | null;
    daysNet: string | null;
    installments: number | null;
}

export type PracticeBlockName = 'contact' | 'address' | 'equipment' | 'doctor' | 'account' | 'risk';

// Practice-wide risk flag + explanatory note, set by admins from the Details
// tab (see components/accounts/practice/details/fieldDefinitions.ts's 'risk'
// group). riskUpdatedAt is server-set (never sent in a PATCH - see
// PracticeDetailUpdate's `risk` block below, which only carries the two
// editable fields).
export interface PracticeRiskBlock {
    riskStatus: PracticeRiskStatus;
    riskNote: string | null;
    riskUpdatedAt: string | null;
}

// Every block is optional on a GET - the caller controls which load via
// `include` (see fetchPracticeDetail, which always requests every block for
// the Details form).
export interface PracticeDetail {
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    patientCount: number;
    // Total logged communications for this practice - always present on the
    // GET response (never omitted the way the blocks below are), so the
    // Communications tab badge can read it straight off the already-loaded
    // practice object with no extra query. Only reflects what was loaded at
    // fetch time - it won't tick up live when one is logged until this
    // practice refetches. Field name is unchanged from the old Calls
    // feature - out of this rework's contract (owned by the practice detail
    // endpoint, not /practices/communications).
    callCount: number;
    contact?: PracticeContactBlock;
    address?: PracticeAddressBlock;
    equipment?: PracticeEquipmentBlock;
    doctor?: PracticeDoctorBlock;
    account?: PracticeAccountBlock;
    risk?: PracticeRiskBlock;
}

// The flat set of field names across every block, plus `name` - this is the
// form's internal model (see fieldDefinitions.ts's PRACTICE_FIELD_DEFS). All
// 30 keys are unique across blocks, so a flat key never collides.
export type PracticeDetailFieldKey =
    | 'name'
    | keyof PracticeContactBlock
    | keyof PracticeAddressBlock
    | keyof PracticeEquipmentBlock
    | keyof PracticeDoctorBlock
    | keyof PracticeAccountBlock
    | keyof Pick<PracticeRiskBlock, 'riskStatus' | 'riskNote'>;

// Symmetric with the GET shape: `name` stays top-level, every block is
// partial (only the keys that actually changed), and the block itself is
// omitted entirely when none of its fields changed.
export interface PracticeDetailUpdate {
    name?: string;
    contact?: Partial<PracticeContactBlock>;
    address?: Partial<PracticeAddressBlock>;
    equipment?: Partial<PracticeEquipmentBlock>;
    doctor?: Partial<PracticeDoctorBlock>;
    account?: Partial<PracticeAccountBlock>;
    risk?: Partial<Pick<PracticeRiskBlock, 'riskStatus' | 'riskNote'>>;
}

const PRACTICE_DETAIL_INCLUDE = 'contact,address,equipment,doctor,account,risk';

export async function fetchPracticeDetail(practiceId: string): Promise<PracticeDetail> {
    const response = await apiV2.get<{ success: boolean; data: PracticeDetail }>(
        `/practices/${practiceId}`,
        { params: { include: PRACTICE_DETAIL_INCLUDE } }
    );
    return response.data.data;
}

export async function updatePracticeDetail(
    practiceId: string,
    changes: PracticeDetailUpdate
): Promise<PracticeDetail> {
    const response = await apiV2.patch<{ success: boolean; data: PracticeDetail }>(
        `/practices/${practiceId}`,
        changes
    );
    return response.data.data;
}

export interface CreatedPractice {
    id: string;
}

/**
 * Create-only counterpart to updatePracticeDetail - same block-structured
 * body shape (see PracticeDetailUpdate), but `name`/`doctor.drFirstName`/
 * `doctor.drLastName` are required (enforced client-side before this is ever
 * called - see practice/create/usePracticeCreateForm.ts). The DB row is
 * awaited server-side before the 201 response, so navigating straight to
 * /accounts/practices/{id} on success is safe.
 */
export async function createPractice(body: PracticeDetailUpdate): Promise<CreatedPractice> {
    const response = await apiV2.post<{ success: boolean; data: CreatedPractice }>(
        '/practices',
        body
    );
    return response.data.data;
}

// Body accepted by POST /practices/:practiceId/bill-com-customer - every
// field optional (an omitted key falls back to the practice's own stored
// value server-side; an explicit null clears it instead of falling back -
// see CreateBillComCustomerModal, which always sends one or the other, never
// omits a key the user could see and edit).
export interface CreateBillComCustomerInput {
    name?: string;
    email?: string | null;
    phone?: string | null;
    address1?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    country?: string | null;
}

export interface CreateBillComCustomerResult {
    billComCustomerId: string;
}

/**
 * Creates a Bill.com customer record for a practice that doesn't have one
 * yet. The backend upserts the returned id into practice_monday_data itself -
 * this call never writes billId back through updatePracticeDetail, so the
 * caller only needs to refetch (see useCreateBillComCustomer).
 */
export async function createBillComCustomer(
    practiceId: string,
    input: CreateBillComCustomerInput
): Promise<CreateBillComCustomerResult> {
    const response = await apiV2.post<{ success: boolean; data: CreateBillComCustomerResult }>(
        `/practices/${practiceId}/bill-com-customer`,
        input
    );
    return response.data.data;
}

// Optional overrides accepted by POST /practices/:practiceId/spark-account-request
// - these are never stored on the practice itself, only folded into the
// composed email (see spark-account.email.ts on the backend). Shared shape
// between the dryRun preview and the real send below so the modal can reuse
// one state object for both.
export interface SparkAccountRequestOverrides {
    requestedUsername?: string;
    doctorType?: string;
    website?: string;
}

export interface SparkAccountRequestPreview {
    subject: string;
    html: string;
    recipients: string[];
}

export interface SparkAccountRequestResult {
    sent: true;
    recipients: string[];
}

/**
 * dryRun:true - the server composes the email from the practice's saved
 * details plus these overrides but never sends it, so this is safe to call
 * on every keystroke (debounced by the caller - see
 * useSparkAccountRequestPreview) to keep RequestSparkAccountModal's preview
 * live.
 */
export async function previewSparkAccountRequest(
    practiceId: string,
    overrides: SparkAccountRequestOverrides
): Promise<SparkAccountRequestPreview> {
    const response = await apiV2.post<{ success: boolean; data: SparkAccountRequestPreview }>(
        `/practices/${practiceId}/spark-account-request`,
        { ...overrides, dryRun: true }
    );
    return response.data.data;
}

/**
 * Sends the same composed email for real - see previewSparkAccountRequest for
 * the dry-run counterpart the modal renders first. Writes nothing to the
 * practice record itself.
 */
export async function sendSparkAccountRequest(
    practiceId: string,
    overrides: SparkAccountRequestOverrides
): Promise<SparkAccountRequestResult> {
    const response = await apiV2.post<{ success: boolean; data: SparkAccountRequestResult }>(
        `/practices/${practiceId}/spark-account-request`,
        overrides
    );
    return response.data.data;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

/**
 * Reads the primary business-rule validation shape produced by the backend:
 * a flat `{ details: { [fieldKey]: message } }` map (e.g. `{ "email": "Must
 * be a well-formed email address" }`) - NOT nested per block, even though the
 * GET/PATCH payload itself is. All 30 field keys are unique across blocks, so
 * a flat key stays unambiguous. Enum/dropdown membership and required-field
 * emptiness are both checked entirely client-side now - see
 * fieldDefinitions.ts's selectFieldError/requiredFieldError.
 */
function fieldErrorsFromFlatMap(details: unknown): Record<string, string> {
    const errors: Record<string, string> = {};
    if (!isRecord(details)) return errors;
    for (const [field, message] of Object.entries(details)) {
        if (typeof message === 'string' && message.trim().length > 0) {
            errors[field] = message;
        }
    }
    return errors;
}

/**
 * Reads the structural (Zod) validation shape produced by the shared
 * `validate` middleware - a separate layer from the flat map above, still
 * used for wrong-type/missing-field rejections before business rules ever
 * run: `{ details: [{ location: 'body', issues: [{ path, message }] }] }`.
 * Only `location: 'body'` issues map to a field on this form; `path` takes
 * the LAST segment so this reads correctly whether the schema reports a flat
 * path (`['email']`) or one nested under a block (`['contact', 'email']`).
 */
function fieldErrorsFromZodShape(details: unknown): Record<string, string> {
    const errors: Record<string, string> = {};
    if (!Array.isArray(details)) return errors;
    for (const locationEntry of details) {
        if (!isRecord(locationEntry) || locationEntry.location !== 'body') continue;
        if (!Array.isArray(locationEntry.issues)) continue;
        for (const issue of locationEntry.issues) {
            if (!isRecord(issue) || !Array.isArray(issue.path) || issue.path.length === 0) continue;
            const field = issue.path[issue.path.length - 1];
            if (typeof field === 'string' && typeof issue.message === 'string') {
                errors[field] = issue.message;
            }
        }
    }
    return errors;
}

/**
 * Pulls per-field validation messages off a rejected PATCH's 400 envelope.
 * The flat map is the primary, expected shape (business-rule failures - bad
 * email, out-of-range number); the Zod array shape is a secondary fallback
 * for structural failures the shared `validate` middleware rejects before
 * business rules run. Returns null when the error carries no field-level
 * detail at all (network failure, 500, etc.) so the caller falls back to a
 * generic banner instead of inventing a field to blame.
 */
export function extractFieldErrors(err: unknown): Record<string, string> | null {
    if (!isRecord(err)) return null;
    const response = err.response;
    if (!isRecord(response) || response.status !== 400) return null;
    const data = response.data;
    if (!isRecord(data) || !isRecord(data.error)) return null;
    const details = data.error.details;

    const fieldErrors = {
        ...fieldErrorsFromFlatMap(details),
        ...fieldErrorsFromZodShape(details)
    };
    return Object.keys(fieldErrors).length > 0 ? fieldErrors : null;
}
